<template>
  <v-container class="pa-0 modern-detail-view">
    <!-- Progress Indicator for Loading -->
    <v-progress-linear
      v-show="loading || internalLoading"
      color="primary"
      indeterminate
      rounded
      height="4"
      class="sticky-progress shadow-sm"
    />

    <!-- Background Refresh Overlay -->
    <v-fade-transition>
      <div v-if="internalLoading" class="refresh-overlay">
        <div class="d-flex flex-column align-center pa-6 rounded-xl bg-white shadow-lg border">
          <v-progress-circular indeterminate color="primary" size="40" width="4" class="mb-4"></v-progress-circular>
          <div class="text-subtitle-2 font-weight-black primary--text text-uppercase tracking-widest">
            Sincronizando Datos...
          </div>
          <div class="caption grey--text font-weight-medium">Actualizando métricas y bitácora</div>
        </div>
      </div>
    </v-fade-transition>

    <div class="pa-6" v-if="work_order && work_order.id" :class="{'opacity-40 pointer-events-none': internalLoading}">
      <!-- Premium Header Section -->
      <v-row class="mb-8 align-start">
        <v-col cols="12" md="8">
          <div class="d-flex align-center flex-wrap gap-3 mb-2">
            <h1 class="text-h3 font-weight-black primary--text mr-4">
              OT #{{ work_order.id }}
            </h1>
            <v-chip
              color="primary lighten-4"
              text-color="primary darken-2"
              label
              class="font-weight-black text-uppercase shadow-sm px-4"
            >
              <v-icon x-small left color="primary darken-2">fas fa-industry</v-icon>
              {{ work_order.completed ? 'Orden Finalizada' : 'En Producción' }}
            </v-chip>
            <v-chip v-if="delayed" color="error lighten-4" text-color="error darken-2" label class="font-weight-black shadow-sm">
              <v-icon x-small left color="error darken-2">fas fa-exclamation-triangle</v-icon>
              Atrasada
            </v-chip>
            <v-chip
              v-if="work_order.reposicion || (work_order.sales_note_data && work_order.sales_note_data.reposicion)"
              color="purple lighten-4"
              text-color="purple darken-2"
              label
              class="font-weight-black text-uppercase shadow-sm px-4"
            >
              <v-icon x-small left color="purple darken-2">fas fa-rotate-right</v-icon>
              Reposición
            </v-chip>
          </div>
          
          <div class="d-flex align-center mt-2 flex-wrap gap-4">
            <div class="caption grey--text font-weight-medium d-flex align-center">
              <v-avatar size="24" color="grey lighten-4" class="mr-2">
                <v-icon x-small color="grey darken-1">fas fa-user-plus</v-icon>
              </v-avatar>
              {{ work_order.created_by.first_name }} {{ work_order.created_by.last_name }} 
              <span class="mx-2 opacity-50">•</span>
              {{ work_order.created_at | moment('DD/MM/YYYY HH:mm') }}
            </div>
            <div v-if="work_order.updated_by" class="caption grey--text font-weight-medium d-flex align-center">
              <v-avatar size="24" color="grey lighten-4" class="mr-2">
                <v-icon x-small color="grey darken-1">fas fa-pen-nib</v-icon>
              </v-avatar>
              {{ work_order.updated_by.first_name }} {{ work_order.updated_by.last_name }}
              <span class="mx-2 opacity-50">•</span>
              {{ work_order.updated_at | moment('DD/MM/YYYY HH:mm') }}
            </div>
          </div>
        </v-col>
        
        <v-col cols="12" md="4" class="text-right">
          <div class="d-flex justify-end align-center gap-2">
            <v-menu offset-y left transition="slide-y-transition" rounded="lg">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="primary" v-bind="attrs" v-on="on" class="grey lighten-4 shadow-sm">
                  <v-icon small>fas fa-ellipsis-v</v-icon>
                </v-btn>
              </template>
              <v-list dense class="py-1">
                <v-list-item class="px-3">
                  <v-btn text small block class="justify-start px-0" @click.stop="printWorkOrder">
                    <v-icon small left>fas fa-print</v-icon> Imprimir
                  </v-btn>
                </v-list-item>
                <v-list-item v-if="$can('readCost', 'SalesNote')" @click="generateCostReport(work_order)" class="px-3">
                  <v-list-item-icon class="mr-3 ml-0"><v-icon small color="blue-grey">fas fa-file-invoice-dollar</v-icon></v-list-item-icon>
                  <v-list-item-title class="body-2 font-weight-medium">Informe de Costos</v-list-item-title>
                </v-list-item>
                
                <v-divider v-if="$can('update', 'WorkOrder')" class="my-1"></v-divider>
                
                <v-list-item v-if="!work_order.completed && $can('update', 'WorkOrder')" @click="update" class="px-3">
                  <v-list-item-icon class="mr-3 ml-0"><v-icon small color="primary">fas fa-edit</v-icon></v-list-item-icon>
                  <v-list-item-title class="body-2 font-weight-medium">Modificar Orden</v-list-item-title>
                </v-list-item>
                
                <v-list-item v-if="$can('update', 'WorkOrder')" @click="addingCost = true" class="px-3">
                  <v-list-item-icon class="mr-3 ml-0"><v-icon small color="orange">fas fa-plus-circle</v-icon></v-list-item-icon>
                  <v-list-item-title class="body-2 font-weight-medium">Agregar Costo Extra</v-list-item-title>
                </v-list-item>

                <v-list-item v-if="relbaseEnabled" @click="showIssuanceDialog = true" class="px-3">
                  <v-list-item-icon class="mr-3 ml-0"><v-icon small color="accent">fas fa-file-invoice-dollar</v-icon></v-list-item-icon>
                  <v-list-item-title class="body-2 font-weight-medium accent--text">Emitir Relbase</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="canEmitBoleta"
                  @click="showBoletaDialog = true"
                  class="px-3"
                >
                  <v-list-item-icon class="mr-3 ml-0"><v-icon small color="success">fas fa-receipt</v-icon></v-list-item-icon>
                  <v-list-item-title class="body-2 font-weight-medium success--text">Emitir boleta</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="simpleFacturaEnabled && $can('create', 'Invoice')" class="px-3">
                  <simple-factura-button :sales_note="work_order.sales_note" />
                </v-list-item>
                <v-list-item v-if="facturacionClEnabled && $can('create', 'Invoice')" class="px-3">
                  <facturacion-cl-button :sales_note="work_order.sales_note" />
                </v-list-item>
                <v-list-item
                  v-if="facturacionClEnabled && $can('read', 'Invoice')"
                  @click="$router.push({ name: 'BulkDispatch', query: { ot: work_order.id } })"
                  class="px-3"
                >
                  <v-list-item-icon class="mr-3 ml-0"><v-icon small color="primary">fas fa-truck-ramp-box</v-icon></v-list-item-icon>
                  <v-list-item-title class="body-2 font-weight-medium">Guías de despacho masivas</v-list-item-title>
                </v-list-item>

                <v-divider v-if="!work_order.completed && $can('update', 'WorkOrder')" class="my-1"></v-divider>
                
                <v-list-item v-if="!work_order.completed && $can('update', 'WorkOrder')" @click="showConfirmCloseOrder = true" :disabled="has_prestage" class="px-3">
                  <v-list-item-icon class="mr-3 ml-0"><v-icon small color="success">fas fa-check-double</v-icon></v-list-item-icon>
                  <v-list-item-title class="body-2 font-weight-medium success--text">Finalizar Orden</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn icon @click="$emit('close')" class="grey lighten-4 shadow-sm">
              <v-icon small>fas fa-times</v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Dashboard Progress -->
      <v-card outlined class="modern-card pa-6 mb-8 bg-primary-gradient border-0">
        <v-row align="center">
          <v-col cols="12" md="3" class="text-center text-md-left border-right-white">
            <div class="white--text text-overline opacity-70 mb-1">Avance Global</div>
            <div class="white--text text-h2 font-weight-black">{{ Math.ceil(workOrderProgress.progress) }}%</div>
          </v-col>
          <v-col cols="12" md="9" class="pa-6">
            <v-progress-linear
              :value="workOrderProgress.progress"
              color="white"
              height="12"
              rounded
              striped
              class="elevation-2"
            />
            <div class="d-flex justify-space-between mt-3">
              <div v-if="show_estimated_work_order" class="white--text caption font-weight-bold d-flex align-center">
                <v-icon x-small color="white" class="mr-2 opacity-70">fas fa-calendar-alt</v-icon>
                ENTREGA ESTIMADA: 
                <span class="ml-2 bg-white primary--text px-2 rounded-sm" v-if="!has_prestage && workOrderProgress.estimated > 0">
                  {{ workOrderProgress.estimated | moment('DD/MM/YYYY') }}
                </span>
                <span v-else-if="has_prestage" class="ml-2 font-italic">En Pre-Prensa</span>
                <span v-else class="ml-2 font-italic">No definida</span>
              </div>
              <div class="white--text caption font-weight-bold d-flex align-center">
                <v-icon x-small color="white" class="mr-2 opacity-70">fas fa-shopping-cart</v-icon>
                CN #{{ work_order.sales_note }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- Detail Information Cards -->
      <v-row class="mb-8">
        <v-col cols="12" md="4">
          <v-card outlined class="modern-card h-100 overflow-hidden">
            <v-sheet color="grey lighten-4" class="px-6 py-3 border-bottom d-flex align-center">
              <v-icon small color="primary" class="mr-3">fas fa-user-circle</v-icon>
              <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 text-uppercase">Cliente y Proyecto</span>
            </v-sheet>
            <v-card-text class="pa-6">
              <div class="mb-4">
                <div class="caption grey--text mb-1 uppercase font-weight-bold">Razón Social</div>
                <div class="body-1 font-weight-black secondary--text lh-1-2">{{ work_order.customer_name }}</div>
              </div>
              <div>
                <div class="caption grey--text mb-1 uppercase font-weight-bold">Nombre del Proyecto</div>
                <div class="body-2 font-weight-medium grey--text text--darken-3">{{ work_order.name }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card outlined class="modern-card h-100 overflow-hidden">
            <v-sheet color="grey lighten-4" class="px-6 py-3 border-bottom d-flex align-center">
              <v-icon small color="primary" class="mr-3">fas fa-clock</v-icon>
              <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 text-uppercase">Tiempos y Logística</span>
            </v-sheet>
            <v-card-text class="pa-6">
              <v-row dense>
                <v-col cols="12" class="mb-3">
                  <div class="caption grey--text mb-1 uppercase font-weight-bold">Entrega Comprometida</div>
                  <div class="body-2 font-weight-black" :class="delayed ? 'error--text' : 'primary--text'">
                    <v-icon x-small :color="delayed ? 'error' : 'primary'" class="mr-1">fas fa-calendar-check</v-icon>
                    {{ work_order.delivery_date | moment('DD/MM/YYYY HH:mm') }}
                  </div>
                </v-col>
                <v-col cols="6" v-if="work_order.install_date">
                  <div class="caption grey--text mb-1 uppercase font-weight-bold">Instalación</div>
                  <div class="caption font-weight-bold grey--text text--darken-3">
                    {{ work_order.install_date | moment('DD/MM/YYYY') }}
                  </div>
                </v-col>
                <v-col cols="6" v-if="work_order.uninstall_date">
                  <div class="caption grey--text mb-1 uppercase font-weight-bold">Desinstalación</div>
                  <div class="caption font-weight-bold grey--text text--darken-3">
                    {{ work_order.uninstall_date | moment('DD/MM/YYYY') }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card outlined class="modern-card h-100 overflow-hidden">
            <v-sheet color="grey lighten-4" class="px-6 py-3 border-bottom d-flex align-center">
              <v-icon small color="primary" class="mr-3">fas fa-align-left</v-icon>
              <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 text-uppercase">Observaciones</span>
            </v-sheet>
            <v-card-text class="pa-6">
              <div class="body-2 font-italic grey--text text--darken-2" v-if="work_order.notes">
                {{ work_order.notes }}
              </div>
              <div class="caption grey--text font-italic text-center py-4" v-else>
                Sin observaciones adicionales
              </div>
              <div v-if="work_order.customer_approval" class="mt-4 pa-3 bg-success-light rounded-sm border-success">
                <div class="caption success--text font-weight-black uppercase mb-1">
                  <v-icon x-small color="success" class="mr-1">fas fa-check-circle</v-icon> Visto Bueno
                </div>
                <div class="caption grey--text text--darken-3 lh-1-2">{{ work_order.approval_notes }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Tabs Section -->
      <v-card outlined class="modern-card elevation-0 overflow-hidden">
        <v-tabs v-model="tab" background-color="grey lighten-5" color="primary" grow class="border-bottom">
          <v-tab class="text-none font-weight-bold py-4">
            <v-icon left small>fas fa-box-open</v-icon> Productos
          </v-tab>
          <v-tab v-if="$can('manage', 'WorkOrder')" class="text-none font-weight-bold py-4">
            <v-icon left small>fas fa-plus-square</v-icon> Adicionales
          </v-tab>
          <v-tab v-if="$can('read', 'PurchaseOrder')" class="text-none font-weight-bold py-4">
            <v-icon left small>fas fa-shopping-bag</v-icon> Compras
          </v-tab>
          <v-tab v-if="$can('read', 'Invoice')" class="text-none font-weight-bold py-4">
            <v-icon left small>fas fa-file-invoice</v-icon> Facturas
          </v-tab>
          <v-tab v-if="$can('readCost', 'SalesNote')" class="text-none font-weight-bold py-4">
            <v-icon left small>fas fa-chart-pie</v-icon> Resumen
          </v-tab>
          <v-tab v-if="$can('read', 'WorkOrder')" class="text-none font-weight-bold py-4">
            <v-icon left small>fas fa-folder-open</v-icon> Documentos
          </v-tab>
          <v-tab v-if="showDistributions" class="text-none font-weight-bold py-4">
            <v-icon left small>fas fa-truck-ramp-box</v-icon> Distribuciones
          </v-tab>
          <v-tab v-if="relbaseEnabled" class="text-none font-weight-bold py-4">
            <v-icon left small style="color:#35BDC8">fas fa-file-invoice-dollar</v-icon>
            <span style="color:#35BDC8">Relbase</span>
            <v-chip v-if="work_order.relbase_documents && work_order.relbase_documents.length" x-small label class="ml-2 font-weight-black" style="background-color:#35BDC8;color:white;">{{ work_order.relbase_documents.length }}</v-chip>
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <!-- Productos Tab -->
          <v-tab-item class="bg-white">
            <v-data-table
              :headers="productsTableHeaders"
              :items="work_order.products"
              :single-expand="true"
              :expanded.sync="expanded"
              item-key="id"
              show-expand
              class="modern-table elevation-0"
              hide-default-footer
              :items-per-page="-1"
            >
              <template v-slot:item.library_product.sku="{ item }">
                <span class="caption grey--text font-weight-bold">{{ item.library_product ? item.library_product.sku : '---' }}</span>
              </template>
              
              <template v-slot:item.name="{ item }">
                <span class="body-2 font-weight-black primary--text">{{ item.name }}</span>
              </template>
              
              <template v-slot:item.qty="{ item }">
                <span class="body-2 font-weight-bold">{{ item.qty }}</span>
              </template>
              
              <template v-slot:item.width="{ item }">
                <span class="body-2 font-weight-medium grey--text text--darken-3">{{ item.width }}</span>
              </template>
              
              <template v-slot:item.height="{ item }">
                <span class="body-2 font-weight-medium grey--text text--darken-3">{{ item.height }}</span>
              </template>
              
              <template v-slot:item.status="{ item }">
                <v-chip
                  v-if="item.in_prestage"
                  :color="item.prestage.color"
                  label
                  outlined
                  x-small
                  class="font-weight-black"
                >
                  {{ item.prestage.name }}
                </v-chip>
                <v-chip
                  v-else-if="!item.completed"
                  label
                  dark
                  color="primary"
                  x-small
                  class="font-weight-black"
                >
                  PRODUCCIÓN
                </v-chip>
                <v-chip v-else label dark color="success" x-small class="font-weight-black">
                  FINALIZADO
                </v-chip>
              </template>
              
              <template v-slot:item.progress="{ item }">
                <div class="d-flex align-center">
                  <v-progress-linear
                    :value="item.progress"
                    :color="item.progress >= 100 ? 'success' : 'accent'"
                    height="6"
                    rounded
                    class="flex-grow-1"
                  />
                  <span class="caption font-weight-black ml-2 primary--text">{{ Math.floor(item.progress) }}%</span>
                </div>
              </template>
              
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-0 expanded-row-container">
                  <div class="pa-6 border-left-primary bg-grey-light">
                    <v-card outlined class="modern-card elevation-2 overflow-hidden bg-white">
                      <product-detail :product="item" @updated="refreshWorkOrder()" :work_order_id="work_order.id" />
                    </v-card>
                  </div>
                </td>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- Adicionales Tab -->
          <v-tab-item v-if="$can('manage', 'WorkOrder')" class="pa-6">
            <v-data-table
              :loading="costLoading"
              :headers="additionalCostsHeaders"
              :items="additionalCosts"
              class="modern-table elevation-0 border rounded"
              hide-default-footer
              :items-per-page="-1"
            >
              <template v-slot:no-data>
                <div class="pa-12 text-center grey--text">
                  <v-icon x-large class="mb-2 opacity-20">fas fa-coins</v-icon>
                  <div class="caption text-uppercase font-weight-bold">No hay costos adicionales registrados</div>
                </div>
              </template>
              <template v-slot:item.price="{ item }">
                <span class="font-weight-black success--text">{{ item.price | moneda }}</span>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn icon small color="error" @click="deleteCost(item)">
                  <v-icon x-small>fas fa-trash-alt</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- Compras Tab -->
          <v-tab-item v-if="$can('read', 'PurchaseOrder')" class="pa-6">
            <v-data-table :headers="purchaseOrdersHeaders" :items="work_order.buying_orders" class="modern-table elevation-0 border rounded">
              <template v-slot:item.created_at="{ item }">{{ item.created_at | moment("DD/MM/YYYY") }}</template>
              <template v-slot:item.total="{ item }">{{ totalOrder(item) | moneda }}</template>
              <template v-slot:item.estatus="{ item }">
                <v-chip x-small label outlined :color="getStatusColor(item)">{{ getStatusName(item) }}</v-chip>
              </template>
            </v-data-table>
          </v-tab-item>
          
          <!-- Facturas Tab -->
          <v-tab-item v-if="$can('read', 'Invoice')" class="pa-6">
            <v-data-table :headers="invoicesHeaders" :items="invoiceItems" class="modern-table elevation-0 border rounded">
              <template v-slot:item.get_total="{ item }">{{ item.get_total | moneda }}</template>
              <template v-slot:item.date_emited="{ item }">{{ item.date_emited | moment('DD/MM/YYYY') }}</template>
              <template v-slot:item.document_id="{ item }">
                <span class="font-weight-black">#{{ item.document_id }}</span>
                <v-chip v-if="item.tipo_display" x-small label outlined class="ml-2">{{ item.tipo_display }}</v-chip>
              </template>
            </v-data-table>
          </v-tab-item>
          
          <!-- Resumen Tab -->
          <v-tab-item v-if="$can('readCost', 'SalesNote')" class="pa-6">
            <running-cost :work_order="work_order" />
          </v-tab-item>
          
          <!-- Documentos Tab -->
          <v-tab-item v-if="$can('read', 'WorkOrder')" class="pa-6">
            <file-manager
              :oldFiles="work_order.files.map(f => ({ ...f, delete: false }))"
              :sales_note="work_order.sales_note"
              @refresh="refreshWorkOrder"
            />
          </v-tab-item>

          <!-- Distribuciones (guías de despacho masivas) Tab -->
          <v-tab-item v-if="showDistributions" class="pa-6">
            <distribution-panel :work-order-id="work_order.id" />
          </v-tab-item>

          <!-- Relbase Tab -->
          <v-tab-item v-if="relbaseEnabled" class="pa-6">
            <v-data-table
              :headers="relbaseDocumentsHeaders"
              :items="work_order.relbase_documents || []"
              class="modern-table elevation-0 border rounded"
              hide-default-footer
              :items-per-page="-1"
              @click:row="openRelbaseDoc"
            >
              <template v-slot:no-data>
                <div class="pa-12 text-center grey--text">
                  <v-icon x-large class="mb-2 opacity-20" style="color:#35BDC8">fas fa-file-invoice-dollar</v-icon>
                  <div class="caption text-uppercase font-weight-bold">No hay documentos Relbase emitidos</div>
                </div>
              </template>
              <template v-slot:item.folio="{ item }">
                <span class="font-weight-black" style="color:#35BDC8">{{ item.folio }}</span>
              </template>
              <template v-slot:item.doc_type_display="{ item }">
                <v-chip x-small label outlined style="border-color:#35BDC8;color:#35BDC8;" class="font-weight-bold">{{ item.doc_type_display }}</v-chip>
              </template>
              <template v-slot:item.date_emited="{ item }">
                <span class="caption grey--text font-weight-medium">{{ item.date_emited | moment('DD/MM/YYYY') }}</span>
              </template>
              <template v-slot:item.total_bruto="{ item }">
                <span class="font-weight-black">{{ item.total_bruto | moneda }}</span>
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </div>

    <!-- Modals -->
    <v-dialog v-model="addingCost" max-width="500" persistent>
      <cost-create-update @close="addingCost = false" @save="addCost" :custom="false" :global="false" :workOrder="true" :cost="newWorkOrderCost" :loading="costLoading" />
    </v-dialog>
    
    <confirm
      v-if="work_order"
      v-model="showConfirmCloseOrder"
      message="¿Estás seguro que deseas marcar esta orden como FINALIZADA?"
      @ok="closeWorkOrder"
      @cancel="showConfirmCloseOrder = false"
    />

    <!-- Relbase Issuance Dialog -->
    <relbase-issuance-dialog
      v-if="relbaseEnabled"
      v-model="showIssuanceDialog"
      :ot-id="work_order.id"
      @issued="refreshWorkOrder"
    />
    <boleta-issuance-dialog
      v-model="showBoletaDialog"
      :ot-id="work_order && work_order.id"
      :work-order="work_order"
      @issued="refreshWorkOrder"
    />
    <relbase-dte-detail-dialog
      v-if="relbaseEnabled"
      v-model="showRelbaseDetail"
      :doc="selectedRelbaseDoc"
    />
  </v-container>
</template>

<script>
import CostCreateUpdate from '@/components/products/costs/CostCreateUpdate'
import RelbaseIssuanceDialog from '@/components/relbase/RelbaseIssuanceDialog.vue'
import RelbaseDteDetailDialog from '@/components/relbase/RelbaseDteDetailDialog.vue'
import BoletaIssuanceDialog from '@/components/sales/BoletaIssuanceDialog.vue'
import SimpleFacturaButton from '@/components/simplefactura/SimpleFacturaButton.vue'
import FacturacionClButton from '@/components/facturacioncl/FacturacionClButton.vue'
import DistributionPanel from '@/components/facturacioncl/DistributionPanel.vue'
import { mapDocumentToInvoiceRow } from '@/api/simplefactura'
import { mapDocumentToInvoiceRow as mapFclDocumentToInvoiceRow } from '@/api/facturacioncl'
import { updateWorkOrder, getWorkOrder } from '@/api/production'
import { getSalesNote, deleteInvoice } from '@/api/salesNotes'
import notifications from '@/utils/notifications'
import { pdfWorkOrder } from '@/doc_generators/workOrder'
import ProductDetail from '@/components/production/workorder/ProductDetail'
import RunningCost from '@/components/production/workorder/RunningCost'
import { postProcessOrderComplete } from '@/api/production'
import FileManager from '@/components/utils/FileManager'
import SoftnetDocumentsChips from '@/components/softnet/SoftnetDocumentsChips'
import SoftnetButton from '@/components/softnet/SoftnetButton.vue' 
import { pdfCostReport } from '@/doc_generators/costReport'
import store from '@/store'

export default {
  name: 'WorkOrderCard',
  components: {
    CostCreateUpdate, ProductDetail, RunningCost,
    FileManager, SoftnetDocumentsChips, SoftnetButton, RelbaseIssuanceDialog, RelbaseDteDetailDialog, BoletaIssuanceDialog, SimpleFacturaButton, FacturacionClButton, DistributionPanel
  },
  props: ['work_order', 'id', 'loading'],
  data() {
    return {
      productsTableHeaders: [
        { text: 'Código', value: 'library_product.sku', class: 'grey lighten-4', width: '120' },
        { text: 'Descripción', value: 'name', class: 'grey lighten-4' },
        { text: 'Cant.', value: 'qty', class: 'grey lighten-4', align: 'end', width: '80' },
        { text: 'Ancho', value: 'width', class: 'grey lighten-4', align: 'end', width: '80' },
        { text: 'Alto', value: 'height', class: 'grey lighten-4', align: 'end', width: '80' },
        { text: 'Estado', value: 'status', sortable: false, class: 'grey lighten-4', align: 'center', width: '150' },
        { text: 'Avance', value: 'progress', sortable: false, class: 'grey lighten-4', width: '150' },
        { text: '', value: 'data-table-expand', class: 'grey lighten-4', width: '50' }
      ],
      additionalCostsHeaders: [
        { text: 'Descripción', value: 'name', class: 'grey lighten-4' },
        { text: 'Monto', value: 'price', class: 'grey lighten-4', align: 'end', width: '120' },
        { text: '', value: 'actions', class: 'grey lighten-4', align: 'center', width: '80', sortable: false }
      ],
      purchaseOrdersHeaders: [
        { text: 'Número', value: 'id', class: 'grey lighten-4' },
        { text: 'Creada', value: 'created_at', class: 'grey lighten-4' },
        { text: 'Proveedor', value: 'provider_name', class: 'grey lighten-4' },
        { text: 'Total', value: 'total', class: 'grey lighten-4', align: 'end' },
        { text: 'Estatus', value: 'estatus', class: 'grey lighten-4', align: 'end' }
      ],
      invoicesHeaders: [
        { text: 'Número', value: 'document_id', class: 'grey lighten-4' },
        { text: 'Fecha', value: 'date_emited', class: 'grey lighten-4' },
        { text: 'Cliente', value: 'rznSocRecep', class: 'grey lighten-4' },
        { text: 'Total', value: 'get_total', class: 'grey lighten-4', align: 'end' }
      ],
      addingCost: false,
      newWorkOrderCost: {
        id: -1, unit: '', name: '', price: 0, min_price: 0, 
        is_supply: true, has_article: false, price_update_option: 0
      },
      costLoading: false,
      showConfirmCloseOrder: false,
      showIssuanceDialog: false,
      showBoletaDialog: false,
      showRelbaseDetail: false,
      selectedRelbaseDoc: null,
      internalLoading: false,
      tab: null,
      expanded: [],
      estatuses: [
        { value: 1, text: 'Ingresada', color: 'info' },
        { value: 2, text: 'Aprobada', color: 'success' },
        { value: 4, text: 'Recepcionada', color: 'grey' },
        { value: 3, text: 'Anulada', color: 'error' }
      ],
      relbaseDocumentsHeaders: [
        { text: 'Folio', value: 'folio', class: 'grey lighten-4', width: '120' },
        { text: 'Tipo', value: 'doc_type_display', class: 'grey lighten-4' },
        { text: 'Fecha', value: 'date_emited', class: 'grey lighten-4', width: '140' },
        { text: 'Total', value: 'total_bruto', class: 'grey lighten-4', align: 'end', width: '150' }
      ]
    }
  },
  computed: {
    relbaseEnabled() {
      return !!this.$store.getters.getCompany?.relbase_integration_enabled
    },
    simpleFacturaEnabled() {
      return !!this.$store.getters.getCompany?.simplefactura_integration_enabled
    },
    facturacionClEnabled() {
      return !!this.$store.getters.getCompany?.facturacioncl_integration_enabled
    },
    showDistributions() {
      return this.facturacionClEnabled && !!this.work_order.sales_note &&
        (this.$can('read', 'SalesNote') || this.$can('read', 'WorkOrder') || this.$can('read', 'Invoice'))
    },
    otHasFactura() {
      const wo = this.work_order || {}
      const rb = wo.relbase_documents || []
      if (rb.some(d => Number(d.doc_type) === 33)) return true
      const sf = wo.simplefactura_documents || []
      if (sf.some(d => Number(d.tipo) === 33)) return true
      const fcl = wo.facturacioncl_documents || []
      if (fcl.some(d => Number(d.tipo) === 33)) return true
      const dtes = wo.dtes || []
      if (dtes.some(d => d.tipo === 30 || d.tipo === 33)) return true
      return false
    },
    otHasBoleta() {
      const wo = this.work_order || {}
      const rb = wo.relbase_documents || []
      if (rb.some(d => Number(d.doc_type) === 39)) return true
      const sf = wo.simplefactura_documents || []
      if (sf.some(d => Number(d.tipo) === 39)) return true
      const fcl = wo.facturacioncl_documents || []
      if (fcl.some(d => Number(d.tipo) === 39)) return true
      const dtes = wo.dtes || []
      if (dtes.some(d => Number(d.tipo) === 39)) return true
      return false
    },
    canEmitBoleta() {
      if (!(this.relbaseEnabled || this.simpleFacturaEnabled || this.facturacionClEnabled)) return false
      if (!this.$can('create', 'Invoice')) return false
      if (this.otHasFactura || this.otHasBoleta) return false
      return true
    },
    invoiceItems() {
      if (this.simpleFacturaEnabled) {
        const docs = this.work_order.simplefactura_documents || []
        return docs.map(doc => mapDocumentToInvoiceRow(doc, this.work_order.customer_name, this.work_order))
      }
      if (this.facturacionClEnabled) {
        const docs = this.work_order.facturacioncl_documents || []
        return docs.map(doc => mapFclDocumentToInvoiceRow(doc, this.work_order.customer_name, this.work_order))
      }
      return (this.work_order.dtes || []).filter(dte => dte.tipo == 30 && dte.estatus < 3)
    },
    delayed() {
      if (!this.workOrderProgress.estimated) return false
      return moment(this.workOrderProgress.estimated).isAfter(moment(this.work_order.delivery_date))
    },
    additionalCosts() {
      if (!this.work_order || !this.work_order.additional_costs) return []
      return Array.isArray(this.work_order.additional_costs) ? this.work_order.additional_costs : []
    },
    has_prestage() {
      return this.work_order.products && this.work_order.products.some(p => p.in_prestage)
    },
    show_estimated_work_order() {
      return this.$store.getters.getCompany.show_estimated_work_order
    },
    workOrderProgress() {
      if (!this.work_order || !this.work_order.products) return { progress: 0, estimated: 0 }
      let completedMts2 = 0
      let estimateds = []
      this.work_order.products.forEach(p => {
        if (p.process_orders && p.process_orders.length > 0) {
          const pEsts = p.process_orders.map(proc => new Date(proc.estimated))
          if (pEsts.length) estimateds.push(Math.max(...pEsts))
          
          // Granular progress calculation per product (average of process progresses)
          const totalProcProgress = p.process_orders.reduce((acc, proc) => {
            if (proc.completed) return acc + 100
            
            const target = proc.machine.production_unit_m2 
              ? (p.mts2 * proc.qtypp) 
              : (p.qty * proc.qtypp + (proc.merma || 0))
            
            const current = proc.machine.production_unit_m2 
              ? (proc.completed_mts2_qty.mts2 || 0) 
              : (proc.completed_mts2_qty.qty || 0)
            
            const procPercent = target > 0 ? (current / target) * 100 : 0
            return acc + Math.min(100, procPercent)
          }, 0)
          
          p.progress = totalProcProgress / p.process_orders.length
        } else {
          p.progress = 0
        }
        completedMts2 += (p.progress / 100) * (p.mts2 || 0)
      })
      return {
        estimated: estimateds.length ? Math.max(...estimateds) : 0,
        progress: this.work_order.total_mts2 > 0 ? (completedMts2 / this.work_order.total_mts2) * 100 : 0
      }
    }
  },
  methods: {
    update() {
      this.$router.push({ name: 'WorkOrderUpdate', params: { id: this.work_order.id } })
    },
    async generateCostReport(item) {
      try {
        const data = await getSalesNote(item.sales_note)
        pdfCostReport(data)
      } catch (err) {
        notifications.showError('Error: ' + err)
      }
    },
    async deleteCost(item) {
      const idx = this.work_order.additional_costs.indexOf(item)
      this.work_order.additional_costs.splice(idx, 1)
      try {
        await updateWorkOrder({ id: this.work_order.id, additional_costs: this.work_order.additional_costs, justUpdateCost: true })
        notifications.showSuccess('Costo eliminado')
      } catch (e) {
        notifications.showError('Error: ' + e)
        this.work_order.additional_costs.push(item)
      }
    },
    async addCost(item) {
      this.costLoading = true
      const cost = { name: item.name, price: item.price }
      if (!Array.isArray(this.work_order.additional_costs)) this.$set(this.work_order, 'additional_costs', [])
      this.work_order.additional_costs.push(cost)
      try {
        const res = await updateWorkOrder({ id: this.work_order.id, additional_costs: this.work_order.additional_costs, justUpdateCost: true })
        this.work_order.additional_costs = res.additional_costs
        notifications.showSuccess('Costo agregado')
        this.addingCost = false
      } catch (e) {
        notifications.showError('Error: ' + e)
        this.work_order.additional_costs.pop()
      } finally {
        this.costLoading = false
      }
    },
    printWorkOrder() {
      pdfWorkOrder(this.work_order, this.$store.getters.getUser)
    },
    openRelbaseDoc(item) {
      if (!item || !item.id) return
      this.selectedRelbaseDoc = item
      this.showRelbaseDetail = true
    },
    async refreshWorkOrder() {
      this.internalLoading = true
      try {
        const data = await getWorkOrder(this.work_order.id)
        this.$emit('update:work_order', data)
        notifications.showSuccess('Información actualizada')
      } catch (e) {
        notifications.showError('Error: ' + e)
      } finally {
        this.internalLoading = false
      }
    },
    totalOrder(item) {
      const total = item.details.reduce((acc, val) => acc + parseFloat(val.qty) * parseFloat(val.price), 0)
      return total - (total * parseFloat(item.discount || 0) / 100)
    },
    getStatusName(item) { return this.estatuses.find(i => i.value == item.estatus).text },
    getStatusColor(item) { return this.estatuses.find(i => i.value == item.estatus).color },
    async closeWorkOrder() {
      this.showConfirmCloseOrder = false
      this.internalLoading = true
      try {
        const { products } = this.work_order
        let pendingCount = 0

        for (const p of (products || [])) {
          if (!p.process_orders) continue
          for (const proc of p.process_orders) {
            if (proc.completed) continue
            pendingCount++

            // Equipo opcional: primero de la máquina si existe, si no null
            const equipmentId = (proc.machine_equipments && proc.machine_equipments.length > 0)
              ? proc.machine_equipments[0].id
              : null

            const bitacoraLen = Array.isArray(proc.bitacora) ? proc.bitacora.length : 0
            await postProcessOrderComplete({
              processes: [{ id: parseInt(proc.id), real_mts2: bitacoraLen === 0 ? parseFloat(p.mts2) : 0, real_qty: bitacoraLen === 0 ? parseFloat(p.qty) : 0, ask_completed: true }],
              equipment: equipmentId,
              notes: 'Cierre automático'
            })
          }
        }

        if (pendingCount === 0) {
          notifications.showWarn('La orden no tenía procesos pendientes por completar')
        } else {
          notifications.showSuccess('OT Finalizada')
        }

        this.$emit('close')
        setTimeout(() => this.$emit('refresh'), 1500)
      } catch (e) {
        notifications.showError('Error: ' + (e.message || e))
      } finally {
        this.internalLoading = false
      }
    }
  }
}
</script>

<style scoped>
.modern-card { border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 12px !important; background-color: #fff; }
.bg-primary-gradient { background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-primary-darken1) 100%) !important; }
.border-right-white { border-right: 1px solid rgba(255,255,255,0.2); }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.bg-grey-light { background-color: #f8f9fa; }
.bg-success-light { background-color: rgba(var(--v-success-base), 0.05); }
.border-success { border: 1px solid rgba(var(--v-success-base), 0.1); }
.border-left-primary { border-left: 4px solid var(--v-primary-base) !important; }
.modern-detail-view { 
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 12px;
}
.lh-1-2 { line-height: 1.25; }
.uppercase { text-transform: uppercase; letter-spacing: 0.5px; }
.sticky-progress { position: sticky; top: 0; z-index: 5; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.gap-4 { gap: 16px; }
.opacity-50 { opacity: 0.5; }
.opacity-70 { opacity: 0.7; }
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important; }
.modern-detail-view >>> .v-tabs-bar { height: auto !important; }
.modern-detail-view >>> .v-window-item { overflow: visible !important; }
.text-none { text-transform: none !important; }
.expanded-row-container { background-color: #f1f3f5; }
.refresh-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
}
.opacity-40 { opacity: 0.4; transition: opacity 0.3s ease; }
.pointer-events-none { pointer-events: none; }
.tracking-widest { letter-spacing: 2px; }
</style>